import React, {useState} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    useBreakpointValue,
    Text,
    IconButton,
    Icon,
    Checkbox,
    Spinner
} from '@chakra-ui/react'
import {CustomClose} from '../icons'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useAccessToken} from '@salesforce/commerce-sdk-react'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'
import {useAuthHelper, AuthHelpers} from '@salesforce/commerce-sdk-react'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'

const BRAND_ID = 'GRG'

const CloseAccountModal = ({isOpen, onClose, onOpen}) => {
    const isMobile = useBreakpointValue({base: true, md: false})
    const [isChecked, setIsChecked] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const navigate = useNavigation()
    const appOrigin = useAppOrigin()
    const logout = useAuthHelper(AuthHelpers.Logout)
    const {app: appConfig} = getConfig()
    const {getTokenWhenReady} = useAccessToken()
    const parameters = {
        ...appConfig.commerceAPI.parameters
    }
    const proxyPath = appConfig.commerceAPI.proxyPath
    const organizationId = parameters.organizationId
    const siteId = parameters.siteId

    const onSignout = async () => {
        const token = await getTokenWhenReady()
        await fetch(`${appOrigin}/process-logout`, {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({token})
        })
        await logout.mutateAsync()
        navigate('/')
    }

    const handleCloseAccount = async () => {
        if (!isChecked) {
            setErrorMessage('Terms and conditions must be accepted')
            return
        }

        setIsLoading(true)
        try {
            const token = await getTokenWhenReady()
            const response = await fetch(
                `${proxyPath}/custom/sfid/v1/organizations/${organizationId}/close-account?siteId=${siteId}`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        closeconsent: isChecked,
                        brand: BRAND_ID
                    })
                }
            )

            if (!response.ok) {
                const errorData = await response.json()
                throw new Error(errorData.message || 'Failed to close account.')
            }

            setErrorMessage('')
            onClose()
            onSignout()
        } catch (error) {
            setErrorMessage('An unexpected error occurred.')
            setIsLoading(false)
        }
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            onOpen={onOpen}
            size={isMobile ? 'full' : 'md'}
            motionPreset="slideInBottom"
        >
            <ModalOverlay />
            <ModalContent
                borderRadius={0}
                position={isMobile ? 'fixed' : 'relative'}
                bottom={isMobile ? '0' : 'auto'}
                m={isMobile ? '0' : 'auto'}
                padding={{base: '12px 16px 32px', lg: '23px 32px 31px'}}
                w={{base: '100%', lg: '441px'}}
                minH="228px"
            >
                <ModalHeader p={0} fontSize="lg" fontWeight="bold">
                    <IconButton
                        onClick={onClose}
                        icon={<Icon as={CustomClose} />}
                        aria-label="Close modal"
                        variant="ghost"
                        size="sm"
                        background="transparent"
                        _hover={{background: 'transparent'}}
                        display="block"
                        marginLeft="auto"
                    />
                </ModalHeader>
                <ModalBody p={0}>
                    <Text
                        variant="accountStatus"
                        textTransform="uppercase"
                        mb={4}
                        mt={{base: 2, lg: '10px'}}
                        lineHeight={{base: '20px '}}
                    >
                        <FormattedMessage
                            defaultMessage="Are you sure you want to close your Loyalty account?"
                            id="close_account_modal.title"
                        />
                    </Text>
                    <Text
                        variant="leaveModalText"
                        mb={{base: 4, lg: 2}}
                        lineHeight={{base: '18px'}}
                    >
                        <FormattedMessage
                            defaultMessage="If you close your account, you will lose access to your accumulated benefits and offers, your purchase history, and will be unsubscribed from our marketing communications for Garage Collectif. If you decide to rejoin our Loyalty program in the future, you will need to create a new account."
                            id="close_account_modal.text"
                        />
                    </Text>
                    <Checkbox
                        isChecked={isChecked}
                        onChange={(e) => {
                            setIsChecked(e.target.checked)
                            if (errorMessage) setErrorMessage('')
                        }}
                        mb={{base: 8, lg: '24px'}}
                        alignItems="flex-start"
                    >
                        <Text fontSize="xs" lineHeight={{base: '15px'}}>
                            <FormattedMessage
                                defaultMessage="I understand that by closing my Loyalty account, I will permanently lose access to my accumulated benefits and offers. If I decide to rejoin the Loyalty program in the future, I will need to create a new account."
                                id="close_account_modal.checkbox_text"
                            />
                        </Text>
                    </Checkbox>
                </ModalBody>

                {errorMessage && (
                    <Text color="orange.600" fontSize="sm" mb={4} textAlign="center">
                        {errorMessage}
                    </Text>
                )}

                <ModalFooter p={0} flexDirection="column" gap={2}>
                    <Button
                        variant="secondary"
                        textTransform="uppercase"
                        w="100%"
                        border="1px solid black"
                        fontSize={{base: 'md'}}
                        lineHeight={{base: '20px'}}
                        pt={{base: '13px'}}
                        pb={{base: '13px'}}
                        h={{base: 'auto'}}
                        onClick={handleCloseAccount}
                        isLoading={isLoading}
                        isDisabled={isLoading}
                        spinner={<Spinner size="sm" />}
                    >
                        <FormattedMessage
                            defaultMessage="Delete my account"
                            id="close_account_modal.close"
                        />
                    </Button>

                    <Button
                        variant="solid"
                        w="100%"
                        fontSize={{base: 'md'}}
                        lineHeight={{base: '20px'}}
                        pt={{base: '14px'}}
                        pb={{base: '14px'}}
                        h={{base: 'auto'}}
                        onClick={onClose}
                    >
                        <FormattedMessage
                            defaultMessage="Keep My Account"
                            id="close_account_modal.keep"
                        />
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    )
}

CloseAccountModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onOpen: PropTypes.func.isRequired
}

export default CloseAccountModal
